import { useEffect, useState } from "react";
import {
  Wrench, Plus, Save, Trash2, Loader2, GripVertical,
  Droplet, Cog, Activity, Circle, Disc, SprayCan, ClipboardCheck, Car, Gauge, Battery, Zap, ShieldCheck, Settings, Fuel,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const ICON_MAP = {
  droplet: Droplet, wrench: Wrench, cog: Cog, activity: Activity, circle: Circle, disc: Disc,
  "spray-can": SprayCan, "clipboard-check": ClipboardCheck, car: Car, gauge: Gauge, battery: Battery,
  zap: Zap, "shield-check": ShieldCheck, settings: Settings, fuel: Fuel,
};
const ICONS = Object.keys(ICON_MAP);
const Ico = ({ name, className }) => {
  const C = ICON_MAP[name] || Wrench;
  return <C className={className} />;
};

const inputCls = "bg-[#050505] border-white/10 rounded-none h-11 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0";

export function ServicesView() {
  const { t } = useLang();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [dragId, setDragId] = useState(null);
  const [nw, setNw] = useState({ label: "", desc: "", duration: "1h", price: "", icon: "wrench" });

  const load = async () => {
    try { const { data } = await api.get("/services"); setServices(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nw.label.trim()) { toast.error(t("admin.svc_required")); return; }
    setSaving("new");
    try {
      await api.post("/services", nw);
      toast.success(t("admin.svc_added"));
      setNw({ label: "", desc: "", duration: "1h", price: "", icon: "wrench" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const setField = (id, k, v) => setServices((prev) => prev.map((s) => (s.id === id ? { ...s, [k]: v } : s)));

  const saveRow = async (s) => {
    setSaving(s.id);
    try {
      await api.patch(`/services/${s.id}`, { label: s.label, desc: s.desc, duration: s.duration, price: s.price, icon: s.icon });
      toast.success(t("admin.svc_saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.svc_delete_confirm"))) return;
    try { await api.delete(`/services/${id}`); toast.success(t("admin.svc_deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...services];
    const from = arr.findIndex((s) => s.id === dragId);
    const to = arr.findIndex((s) => s.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setServices(arr);
    setDragId(null);
    try { await api.post("/services/reorder", { ids: arr.map((s) => s.id) }); toast.success(t("admin.svc_reordered")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const IconSelect = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full bg-[#050505] border-white/10 rounded-none h-11 text-white focus:ring-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#0C0C0E] border-white/10 text-white rounded-none max-h-64">
        {ICONS.map((ic) => (
          <SelectItem key={ic} value={ic} className="focus:bg-white/10 rounded-none">
            <span className="flex items-center gap-2"><Ico name={ic} className="w-4 h-4 text-[#E60000]" /> {ic}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="max-w-4xl" data-testid="services-view">
      <h3 className="font-display font-700 text-white text-lg tracking-tight mb-1 flex items-center gap-2">
        <Wrench className="w-4 h-4 text-[#E60000]" /> {t("admin.svc_title")}
      </h3>
      <p className="text-zinc-500 text-xs mb-5 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" /> {t("admin.svc_drag_hint")}</p>

      {/* Add form */}
      <div className="border border-white/10 bg-[#0C0C0E] p-6 mb-8">
        <h4 className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> {t("admin.svc_add")}</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.svc_label")}</label>
            <Input value={nw.label} onChange={(e) => setNw((p) => ({ ...p, label: e.target.value }))} data-testid="service-add-label" placeholder="Ex: Climatisation" className={inputCls} />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.svc_price")}</label>
            <Input value={nw.price} onChange={(e) => setNw((p) => ({ ...p, price: e.target.value }))} data-testid="service-add-price" placeholder="dès 49€ / Sur devis" className={inputCls} />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.svc_duration")}</label>
            <Input value={nw.duration} onChange={(e) => setNw((p) => ({ ...p, duration: e.target.value }))} data-testid="service-add-duration" placeholder="1h" className={inputCls} />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.svc_icon")}</label>
            <IconSelect value={nw.icon} onChange={(v) => setNw((p) => ({ ...p, icon: v }))} testid="service-add-icon" />
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.svc_desc")}</label>
            <Textarea value={nw.desc} onChange={(e) => setNw((p) => ({ ...p, desc: e.target.value }))} data-testid="service-add-desc" className="bg-[#050505] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[70px]" />
          </div>
        </div>
        <button onClick={add} disabled={saving === "new"} data-testid="service-add-submit" className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
          {saving === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("admin.svc_add")}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : services.length === 0 ? (
        <div className="border border-white/10 bg-[#0C0C0E] py-16 text-center text-zinc-500 font-mono-tech text-sm" data-testid="services-empty">{t("admin.svc_empty")}</div>
      ) : (
        <div className="space-y-px bg-white/10 border border-white/10">
          {services.map((s) => (
            <div
              key={s.id}
              draggable
              onDragStart={() => setDragId(s.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.id)}
              className={`bg-[#0C0C0E] p-5 transition-opacity ${dragId === s.id ? "opacity-40" : ""}`}
              data-testid={`service-row-${s.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-[#00E5FF] transition-colors" data-testid={`service-drag-${s.id}`}><GripVertical className="w-5 h-5" /></span>
                <span className="w-10 h-10 shrink-0 flex items-center justify-center border border-[#E60000]/40 bg-[#E60000]/10"><Ico name={s.icon} className="w-5 h-5 text-[#E60000]" /></span>
                <Input value={s.label} onChange={(e) => setField(s.id, "label", e.target.value)} data-testid={`service-label-${s.id}`} className={`${inputCls} flex-1`} />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">{t("admin.svc_duration")}</label>
                  <Input value={s.duration} onChange={(e) => setField(s.id, "duration", e.target.value)} data-testid={`service-duration-${s.id}`} className={inputCls} />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">{t("admin.svc_price")}</label>
                  <Input value={s.price || ""} onChange={(e) => setField(s.id, "price", e.target.value)} data-testid={`service-price-input-${s.id}`} className={inputCls} />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">{t("admin.svc_icon")}</label>
                  <IconSelect value={s.icon} onChange={(v) => setField(s.id, "icon", v)} testid={`service-icon-${s.id}`} />
                </div>
              </div>
              <Textarea value={s.desc} onChange={(e) => setField(s.id, "desc", e.target.value)} data-testid={`service-desc-${s.id}`} className="mt-3 bg-[#050505] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[44px]" />
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => saveRow(s)} disabled={saving === s.id} data-testid={`service-save-${s.id}`} className="inline-flex items-center gap-2 px-4 py-2 border border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 font-mono-tech text-xs uppercase tracking-wider transition-colors disabled:opacity-50">
                  {saving === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t("admin.svc_save")}
                </button>
                <button onClick={() => del(s.id)} data-testid={`service-delete-${s.id}`} className="w-9 h-9 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
